import { app } from 'electron';
import { getSupabase } from './supabase/client';
import { t } from './language';
import { CHANNELS } from '../ipc/channels';
import { getMainWindow } from '../windows/mainWindow';

/**
 * TalkNote sunucusunun API'si (web/ uygulaması). Gizli anahtar isteyen işler
 * masaüstünden buraya taşınıyor; masaüstü yalnızca Supabase oturum token'ını
 * `Authorization: Bearer` ile gönderir, sunucu imzayı doğrular.
 *
 * Adres `API_URL` (yoksa eski adıyla `WEB_APP_URL`) ortam değişkeninden gelir.
 */

const DEFAULT_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId?: string,
    /** Sunucunun makinece okunur nedeni (ör. `quota`, `consent_required`). */
    readonly reason?: string,
  ) {
    super(message);
  }
}

export function apiBaseUrl(): string | null {
  const url = (process.env.API_URL || process.env.WEB_APP_URL)?.trim();
  return url ? url.replace(/\/+$/, '') : null;
}

async function accessToken(forceRefresh: boolean): Promise<string> {
  const auth = getSupabase().auth;
  const { data } = forceRefresh ? await auth.refreshSession() : await auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(t('Oturum bulunamadı — yeniden giriş yapın.', 'No active session — please sign in again.'), 401);
  return token;
}

interface RequestOptions {
  body?: unknown;
  timeoutMs?: number;
}

async function send(
  base: string,
  method: string,
  path: string,
  token: string,
  options: RequestOptions,
): Promise<Response> {
  try {
    return await fetch(`${base}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        // Web uygulaması aynı modülü kullanıyor; adını derlemede kendisi veriyor.
        'X-Client': `${process.env.TALKNOTE_CLIENT_NAME || 'talknote-desktop'}/${app.getVersion()}`,
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: AbortSignal.timeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS),
    });
  } catch {
    throw new ApiError(t(`TalkNote sunucusuna ulaşılamadı (${base}).`, `Could not reach the TalkNote server (${base}).`), 0);
  }
}

/**
 * Sunucuya kimlikli istek. Token süresi dolmuşsa (401 `expired`) oturumu bir
 * kez tazeleyip yeniden dener; 429'da sunucunun bekleme süresini söyler.
 */
export async function apiRequest<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, options: RequestOptions = {}): Promise<T> {
  const base = apiBaseUrl();
  if (!base) throw new ApiError(t('API_URL tanımlı değil (ör. https://api.talknote.net).', 'API_URL is not set (e.g. https://api.talknote.net).'), 0);

  let res = await send(base, method, path, await accessToken(false), options);
  let json = (await res.json().catch(() => ({}))) as T & { error?: string; reason?: string; requestId?: string };

  if (res.status === 401 && json.reason === 'expired') {
    res = await send(base, method, path, await accessToken(true), options);
    json = (await res.json().catch(() => ({}))) as typeof json;
  }

  if (res.ok) return json;

  const requestId = res.headers.get('x-request-id') ?? json.requestId;
  if (res.status === 429) {
    const wait = Number(res.headers.get('retry-after')) || 60;
    throw new ApiError(t(`Çok fazla istek gönderildi, ${wait} sn sonra tekrar dene.`, `Too many requests — try again in ${wait}s.`), 429, requestId);
  }
  // Yasal metinlerin yeni sürümü onaylanmadı: renderer onay ekranını açsın.
  if (json.reason === 'consent_required') notifyConsentRequired();
  // Bu sürüm artık desteklenmiyor (yönetim konsolu > Sistem): renderer "Güncelleme gerekli" ekranını açsın.
  if (json.reason === 'upgrade_required') {
    const { minVersion } = json as { minVersion?: unknown };
    notifyUpdateRequired(typeof minVersion === 'string' ? minVersion : null);
    throw new ApiError(
      t('TalkNote’un bu sürümü artık desteklenmiyor. Devam etmek için güncelle.', 'This version of TalkNote is no longer supported. Please update to continue.'),
      res.status,
      requestId,
      json.reason,
    );
  }
  // Yönetim konsolu: hesap askıda / özellik acil durdurmada — sunucu mesajı Türkçe, arayüz diline çevir.
  if (json.reason === 'banned') {
    throw new ApiError(
      t(`Hesabın askıya alındı. ${json.error?.replace(/^Hesabın askıya alındı[:.]?\s*/, '') ?? ''}`.trim(), 'Your account has been suspended. Contact support for details.'),
      res.status,
      requestId,
      json.reason,
    );
  }
  if (json.reason === 'paused') {
    throw new ApiError(
      t('Bu özellik bakım nedeniyle geçici olarak kapalı. Biraz sonra tekrar dene.', 'This feature is temporarily paused for maintenance. Please try again later.'),
      res.status,
      requestId,
      json.reason,
    );
  }
  throw new ApiError(
    json.error || t(`İstek başarısız (HTTP ${res.status}).`, `Request failed (HTTP ${res.status}).`),
    res.status,
    requestId,
    json.reason,
  );
}

function notifyConsentRequired(): void {
  const window = getMainWindow();
  if (window && !window.isDestroyed()) window.webContents.send(CHANNELS.consentRequired);
}

function notifyUpdateRequired(minVersion: string | null): void {
  const window = getMainWindow();
  if (window && !window.isDestroyed()) window.webContents.send(CHANNELS.updateRequired, minVersion);
}
